import {
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Typography,
} from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import type {
    KboAvailableModel,
    KboLlmSettings,
    KboSettings,
} from "components/KBo/Models/KboSettings";
import type { LLMGenerationSettings } from "models/LLMs";
import React, { useCallback, useMemo } from "react";
import {
    renderSettingsFieldLabel,
    SettingsFieldLabel,
} from "./SettingsFieldLabel";
import { KBoModelGenerationSettings } from "./KBoModelGenerationSettings";

interface Props {
    settings: KboSettings;
    availableModels: KboAvailableModel[];
    availableEmbeddingModels: string[];
    onChange: (patch: Partial<KboSettings>) => void;
    defaults?: KboSettings | null;
}

export function KBoAdvancedConfiguration({
    settings,
    availableModels,
    availableEmbeddingModels,
    onChange,
    defaults,
}: Props) {
    const embeddingModelOptions = useMemo(
        () =>
            availableEmbeddingModels.map((model) => ({
                label: model,
                value: model,
            })),
        [availableEmbeddingModels],
    );

    const updateLlmPatch = useCallback(
        (patch: Partial<KboLlmSettings>) => {
            onChange({ llm: { ...settings.llm, ...patch } });
        },
        [settings.llm, onChange],
    );

    const updateEmbedding = useCallback(
        (field: string, value: string | number | null) => {
            onChange({
                embedding: { ...settings.embedding, [field]: value },
            });
        },
        [settings.embedding, onChange],
    );

    const updateChatHistory = useCallback(
        (field: string, value: string) => {
            const num = parseInt(value, 10);
            onChange({
                chatHistory: {
                    ...settings.chatHistory,
                    [field]: isNaN(num) ? null : Math.max(num, 1),
                },
            });
        },
        [settings.chatHistory, onChange],
    );

    const updateMemory = useCallback(
        (field: string, value: string) => {
            const num = parseInt(value, 10);
            onChange({
                memory: {
                    ...settings.memory,
                    [field]: isNaN(num) ? null : Math.max(num, 0),
                },
            });
        },
        [settings.memory, onChange],
    );

    const updateSearch = useCallback(
        (field: string, value: string | number | null) => {
            onChange({
                search: { ...settings.search, [field]: value },
            });
        },
        [settings.search, onChange],
    );

    const updateQuery = useCallback(
        (field: string, value: string) => {
            const num = parseInt(value, 10);
            onChange({
                query: {
                    ...settings.query,
                    [field]: isNaN(num) ? null : Math.max(num, 1),
                },
            });
        },
        [settings.query, onChange],
    );

    const updateMessages = useCallback(
        (field: string, value: string | null) => {
            onChange({
                messages: { ...settings.messages, [field]: value },
            });
        },
        [settings.messages, onChange],
    );

    const mainAgentValue = settings.llm?.mainAgent ?? null;
    const mainAgentDefault = defaults?.llm?.mainAgent ?? null;
    const powerPointAgentValue = settings.llm?.powerPointAgent ?? null;
    const powerPointAgentDefault = defaults?.llm?.powerPointAgent ?? null;
    const reducerValue = settings.llm?.reducer ?? null;
    const reducerDefault = defaults?.llm?.reducer ?? null;

    const updateMainAgent = useCallback(
        (value: LLMGenerationSettings | null) => {
            updateLlmPatch({
                mainAgent: value,
            });
        },
        [updateLlmPatch],
    );

    const updateReducer = useCallback(
        (value: LLMGenerationSettings | null) => {
            updateLlmPatch({
                reducer: value,
            });
        },
        [updateLlmPatch],
    );

    const updatePowerPointAgent = useCallback(
        (value: LLMGenerationSettings | null) => {
            updateLlmPatch({
                powerPointAgent: value,
            });
        },
        [updateLlmPatch],
    );

    return (
        <Grid container direction="column" rowGap={3}>
            {/* LLM Models + Temperatures */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">LLM Models</Typography>
                        </Grid>
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                        >
                            <Grid item xs={4}>
                                <KBoModelGenerationSettings
                                    id="kbo-main-agent"
                                    availableModels={availableModels}
                                    value={mainAgentValue}
                                    defaultValue={mainAgentDefault}
                                    modelLabel="Main Agent Model"
                                    temperatureLabel="Main Agent Temperature"
                                    renderLabel={renderSettingsFieldLabel}
                                    onChange={updateMainAgent}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <KBoModelGenerationSettings
                                    id="kbo-reducer"
                                    availableModels={availableModels}
                                    value={reducerValue}
                                    defaultValue={reducerDefault}
                                    modelLabel="Reducer Model"
                                    temperatureLabel="Reducer Temperature"
                                    renderLabel={renderSettingsFieldLabel}
                                    onChange={updateReducer}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <KBoModelGenerationSettings
                                    id="kbo-powerpoint-agent"
                                    availableModels={availableModels}
                                    value={powerPointAgentValue}
                                    defaultValue={powerPointAgentDefault}
                                    modelLabel="PowerPoint Agent Model"
                                    temperatureLabel="PowerPoint Agent Temperature"
                                    renderLabel={renderSettingsFieldLabel}
                                    onChange={updatePowerPointAgent}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Embedding */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">
                                Embedding Configuration
                            </Typography>
                        </Grid>
                        <EmbeddingRow
                            label="Tool Selection"
                            modelId="kbo-adv-tool-selection-model"
                            modelValue={
                                settings.embedding?.toolSelectionModel
                            }
                            onModelChange={(v) =>
                                updateEmbedding("toolSelectionModel", v)
                            }
                            modelOptions={embeddingModelOptions}
                            defaultModel={
                                defaults?.embedding?.toolSelectionModel
                            }
                        />
                        <EmbeddingRow
                            label="Sound Clip"
                            modelId="kbo-adv-sound-clip-model"
                            similarityValue={
                                settings.embedding?.soundClipMinSimilarity
                            }
                            onSimilarityChange={(v) =>
                                updateEmbedding("soundClipMinSimilarity", v)
                            }
                            defaultModel={defaults?.embedding?.soundClipModel}
                            defaultSimilarity={
                                defaults?.embedding?.soundClipMinSimilarity
                            }
                        />
                        <EmbeddingRow
                            label="Transcription"
                            modelId="kbo-adv-transcription-model"
                            similarityValue={
                                settings.embedding?.transcriptionMinSimilarity
                            }
                            onSimilarityChange={(v) =>
                                updateEmbedding(
                                    "transcriptionMinSimilarity",
                                    v,
                                )
                            }
                            defaultModel={
                                defaults?.embedding?.transcriptionModel
                            }
                            defaultSimilarity={
                                defaults?.embedding?.transcriptionMinSimilarity
                            }
                        />
                        <EmbeddingRow
                            label="Knowledge Base"
                            modelId="kbo-adv-knowledge-base-model"
                            similarityValue={
                                settings.embedding?.knowledgeBaseMinSimilarity
                            }
                            onSimilarityChange={(v) =>
                                updateEmbedding(
                                    "knowledgeBaseMinSimilarity",
                                    v,
                                )
                            }
                            defaultModel={
                                defaults?.embedding?.knowledgeBaseModel
                            }
                            defaultSimilarity={
                                defaults?.embedding?.knowledgeBaseMinSimilarity
                            }
                        />
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                        >
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Max Embedding Messages"
                                    isDefault={
                                        settings.embedding
                                            ?.maxEmbeddingMessages == null
                                    }
                                    onReset={() =>
                                        updateEmbedding(
                                            "maxEmbeddingMessages",
                                            null,
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-max-embedding-messages"
                                    type="number"
                                    value={
                                        settings.embedding
                                            ?.maxEmbeddingMessages ??
                                        defaults?.embedding
                                            ?.maxEmbeddingMessages ??
                                        ""
                                    }
                                    onChange={(e) => {
                                        const num = parseInt(
                                            e.target.value,
                                            10,
                                        );
                                        updateEmbedding(
                                            "maxEmbeddingMessages",
                                            isNaN(num) ? null : Math.max(num, 1),
                                        );
                                    }}
                                    inputProps={{ min: 1, max: 100 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Max Embedding Tokens"
                                    isDefault={
                                        settings.embedding
                                            ?.maxEmbeddingTokens == null
                                    }
                                    onReset={() =>
                                        updateEmbedding(
                                            "maxEmbeddingTokens",
                                            null,
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-max-embedding-tokens"
                                    type="number"
                                    value={
                                        settings.embedding
                                            ?.maxEmbeddingTokens ??
                                        defaults?.embedding
                                            ?.maxEmbeddingTokens ??
                                        ""
                                    }
                                    onChange={(e) => {
                                        const num = parseInt(
                                            e.target.value,
                                            10,
                                        );
                                        updateEmbedding(
                                            "maxEmbeddingTokens",
                                            isNaN(num) ? null : Math.max(num, 1),
                                        );
                                    }}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Chat History */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">
                                Chat History Reduction
                            </Typography>
                        </Grid>
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                        >
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Reduce After Token Count"
                                    isDefault={
                                        settings.chatHistory
                                            ?.reduceAfterTokenCount == null
                                    }
                                    onReset={() =>
                                        onChange({
                                            chatHistory: {
                                                ...settings.chatHistory,
                                                reduceAfterTokenCount: null,
                                            },
                                        })
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-reduce-after-tokens"
                                    type="number"
                                    value={
                                        settings.chatHistory
                                            ?.reduceAfterTokenCount ??
                                        defaults?.chatHistory
                                            ?.reduceAfterTokenCount ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        updateChatHistory(
                                            "reduceAfterTokenCount",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1000 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Reduce To Message Count"
                                    isDefault={
                                        settings.chatHistory
                                            ?.reduceToMessageCount == null
                                    }
                                    onReset={() =>
                                        onChange({
                                            chatHistory: {
                                                ...settings.chatHistory,
                                                reduceToMessageCount: null,
                                            },
                                        })
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-reduce-to-messages"
                                    type="number"
                                    value={
                                        settings.chatHistory
                                            ?.reduceToMessageCount ??
                                        defaults?.chatHistory
                                            ?.reduceToMessageCount ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        updateChatHistory(
                                            "reduceToMessageCount",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 50 }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Memory */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">Memory</Typography>
                        </Grid>
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                        >
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Default Recall Limit"
                                    isDefault={
                                        settings.memory?.defaultRecallLimit ==
                                        null
                                    }
                                    onReset={() =>
                                        onChange({
                                            memory: {
                                                ...settings.memory,
                                                defaultRecallLimit: null,
                                            },
                                        })
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-recall-limit"
                                    type="number"
                                    value={
                                        settings.memory?.defaultRecallLimit ??
                                        defaults?.memory?.defaultRecallLimit ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        updateMemory(
                                            "defaultRecallLimit",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 50 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Similar Conversation Months"
                                    isDefault={
                                        settings.memory
                                            ?.similarConversationMonths == null
                                    }
                                    onReset={() =>
                                        onChange({
                                            memory: {
                                                ...settings.memory,
                                                similarConversationMonths: null,
                                            },
                                        })
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-similar-conv-months"
                                    type="number"
                                    value={
                                        settings.memory
                                            ?.similarConversationMonths ??
                                        defaults?.memory
                                            ?.similarConversationMonths ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        updateMemory(
                                            "similarConversationMonths",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 0, max: 24 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Similar Conversation Days"
                                    isDefault={
                                        settings.memory
                                            ?.similarConversationDays == null
                                    }
                                    onReset={() =>
                                        onChange({
                                            memory: {
                                                ...settings.memory,
                                                similarConversationDays: null,
                                            },
                                        })
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-similar-conv-days"
                                    type="number"
                                    value={
                                        settings.memory
                                            ?.similarConversationDays ??
                                        defaults?.memory
                                            ?.similarConversationDays ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        updateMemory(
                                            "similarConversationDays",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 0, max: 365 }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Search */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">Search</Typography>
                        </Grid>
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                        >
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Reranking Candidate Multiplier"
                                    isDefault={
                                        settings.search
                                            ?.rerankingCandidateMultiplier ==
                                        null
                                    }
                                    onReset={() =>
                                        updateSearch(
                                            "rerankingCandidateMultiplier",
                                            null,
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-reranking-multiplier"
                                    type="number"
                                    value={
                                        settings.search
                                            ?.rerankingCandidateMultiplier ??
                                        defaults?.search
                                            ?.rerankingCandidateMultiplier ??
                                        ""
                                    }
                                    onChange={(e) => {
                                        const num = parseInt(
                                            e.target.value,
                                            10,
                                        );
                                        updateSearch(
                                            "rerankingCandidateMultiplier",
                                            isNaN(num)
                                                ? null
                                                : Math.max(num, 1),
                                        );
                                    }}
                                    inputProps={{ min: 1, max: 100 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Suggestion Similarity Floor"
                                    isDefault={
                                        settings.search
                                            ?.suggestionSimilarityFloor == null
                                    }
                                    onReset={() =>
                                        updateSearch(
                                            "suggestionSimilarityFloor",
                                            null,
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-suggestion-similarity"
                                    type="number"
                                    value={
                                        settings.search
                                            ?.suggestionSimilarityFloor ??
                                        defaults?.search
                                            ?.suggestionSimilarityFloor ??
                                        ""
                                    }
                                    onChange={(e) => {
                                        const num = parseFloat(e.target.value);
                                        updateSearch(
                                            "suggestionSimilarityFloor",
                                            isNaN(num)
                                                ? null
                                                : Math.min(
                                                      Math.max(num, 0),
                                                      1,
                                                  ),
                                        );
                                    }}
                                    inputProps={{
                                        min: 0,
                                        max: 1,
                                        step: 0.05,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Query */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">Query</Typography>
                        </Grid>
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                        >
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="SQL Timeout (seconds)"
                                    isDefault={
                                        settings.query?.sqlTimeoutSeconds ==
                                        null
                                    }
                                    onReset={() =>
                                        onChange({
                                            query: {
                                                ...settings.query,
                                                sqlTimeoutSeconds: null,
                                            },
                                        })
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-sql-timeout"
                                    type="number"
                                    value={
                                        settings.query?.sqlTimeoutSeconds ??
                                        defaults?.query?.sqlTimeoutSeconds ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        updateQuery(
                                            "sqlTimeoutSeconds",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 600 }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <SettingsFieldLabel
                                    label="Settings Cache TTL (minutes)"
                                    isDefault={
                                        settings.query
                                            ?.settingsCacheTtlMinutes == null
                                    }
                                    onReset={() =>
                                        onChange({
                                            query: {
                                                ...settings.query,
                                                settingsCacheTtlMinutes: null,
                                            },
                                        })
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-cache-ttl"
                                    type="number"
                                    value={
                                        settings.query
                                            ?.settingsCacheTtlMinutes ??
                                        defaults?.query
                                            ?.settingsCacheTtlMinutes ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        updateQuery(
                                            "settingsCacheTtlMinutes",
                                            e.target.value,
                                        )
                                    }
                                    inputProps={{ min: 1, max: 60 }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Messages */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">Messages</Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                User-facing strings and error messages.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <SettingsFieldLabel
                                label="Unable to Process Request"
                                isDefault={
                                    settings.messages
                                        ?.unableToProcessRequest == null
                                }
                                onReset={() =>
                                    updateMessages(
                                        "unableToProcessRequest",
                                        null,
                                    )
                                }
                            />
                            <AcxMainTextField
                                id="kbo-msg-unable"
                                fullWidth
                                value={
                                    settings.messages
                                        ?.unableToProcessRequest ??
                                    defaults?.messages
                                        ?.unableToProcessRequest ??
                                    ""
                                }
                                onChange={(e) =>
                                    updateMessages(
                                        "unableToProcessRequest",
                                        e.target.value || null,
                                    )
                                }
                            />
                        </Grid>
                        <Grid item>
                            <SettingsFieldLabel
                                label="No Data Found"
                                isDefault={
                                    settings.messages?.noDataFound == null
                                }
                                onReset={() =>
                                    updateMessages("noDataFound", null)
                                }
                            />
                            <AcxMainTextField
                                id="kbo-msg-no-data"
                                fullWidth
                                value={
                                    settings.messages?.noDataFound ??
                                    defaults?.messages?.noDataFound ??
                                    ""
                                }
                                onChange={(e) =>
                                    updateMessages(
                                        "noDataFound",
                                        e.target.value || null,
                                    )
                                }
                            />
                        </Grid>
                        <Grid item>
                            <SettingsFieldLabel
                                label="Hierarchy Synonyms"
                                isDefault={
                                    settings.messages?.hierarchySynonyms ==
                                    null
                                }
                                onReset={() =>
                                    updateMessages(
                                        "hierarchySynonyms",
                                        null,
                                    )
                                }
                            />
                            <AcxMainTextField
                                id="kbo-msg-hierarchy"
                                fullWidth
                                value={
                                    settings.messages?.hierarchySynonyms ??
                                    defaults?.messages?.hierarchySynonyms ??
                                    ""
                                }
                                onChange={(e) =>
                                    updateMessages(
                                        "hierarchySynonyms",
                                        e.target.value || null,
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Experimental */}
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">Experimental</Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Features under active development. These may
                                change or be removed.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={
                                            (settings.experimental
                                                ?.allowUsersToEditFilters as
                                                | boolean
                                                | undefined) ?? false
                                        }
                                        onChange={(_, checked) =>
                                            onChange({
                                                experimental: {
                                                    ...settings.experimental,
                                                    allowUsersToEditFilters:
                                                        checked,
                                                },
                                            })
                                        }
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        Allow users to edit filters directly
                                    </Typography>
                                }
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
}

interface EmbeddingRowProps {
    label: string;
    modelId: string;
    modelValue?: string | null;
    onModelChange?: (value: string | null) => void;
    modelOptions?: { label: string; value: string }[];
    similarityValue?: number | null;
    onSimilarityChange?: (value: number | null) => void;
    defaultModel?: string | null;
    defaultSimilarity?: number | null;
}

function EmbeddingRow({
    label,
    modelId,
    modelValue,
    onModelChange,
    modelOptions = [],
    similarityValue,
    onSimilarityChange,
    defaultModel,
    defaultSimilarity,
}: EmbeddingRowProps) {
    const effectiveModel = modelValue ?? defaultModel;
    const effectiveSimilarity = similarityValue ?? defaultSimilarity;

    return (
        <Grid container item columnGap={2} flexWrap="nowrap" alignItems="end">
            <Grid item xs={3}>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, paddingBottom: "8px" }}
                >
                    {label}
                </Typography>
            </Grid>
            <Grid item xs={5}>
                <SettingsFieldLabel
                    label="Model"
                    isDefault={modelValue == null}
                    onReset={onModelChange ? () => onModelChange(null) : undefined}
                />
                {onModelChange ? (
                    <AcxSelect
                        key={`${modelId}-${effectiveModel ?? ""}`}
                        id={modelId}
                        options={modelOptions}
                        isClearable
                        defaultValue={
                            effectiveModel
                                ? {
                                      label: effectiveModel,
                                      value: effectiveModel,
                                  }
                                : undefined
                        }
                        placeholder="Select embedding model..."
                        onChange={(opt: { value: string } | null) =>
                            onModelChange(opt?.value ?? null)
                        }
                    />
                ) : (
                    <AcxMainTextField
                        id={modelId}
                        value={effectiveModel ?? ""}
                        isDisabled
                    />
                )}
            </Grid>
            {/* Always reserve the similarity column so the Model box aligns
                across every row, even when a row has no Min Similarity. */}
            <Grid item xs={4}>
                {onSimilarityChange && (
                    <>
                        <SettingsFieldLabel
                            label="Min Similarity"
                            isDefault={similarityValue == null}
                            onReset={() => onSimilarityChange(null)}
                        />
                        <AcxMainTextField
                            id={`${modelId}-similarity`}
                            type="number"
                            value={effectiveSimilarity ?? ""}
                            onChange={(e) => {
                                const num = parseFloat(e.target.value);
                                onSimilarityChange(
                                    isNaN(num)
                                        ? null
                                        : Math.min(Math.max(num, 0), 1),
                                );
                            }}
                            inputProps={{ min: 0, max: 1, step: 0.05 }}
                        />
                    </>
                )}
            </Grid>
        </Grid>
    );
}
